import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

function SubRow({ s, count, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(s.name);
  const [sure, setSure] = useState(false);
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChanged();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  return (
    <div className="flex items-center justify-between gap-2 py-2" data-testid={`subcategory-row-${s.id}`}>
      {edit ? (
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8" autoFocus data-testid={`subcategory-rename-input-${s.id}`} />
      ) : (
        <span className="text-sm">{s.name} <span className="font-mono-data text-xs text-muted-foreground">· {count} product{count === 1 ? "" : "s"}</span></span>
      )}
      <div className="flex shrink-0 gap-1">
        {edit ? (
          <>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => run(() => api.put(`/admin/subcategories/${s.id}`, { name }), "Renamed").then((ok) => ok && setEdit(false))} data-testid={`subcategory-rename-save-${s.id}`}><Check className="h-4 w-4 text-emerald-400" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEdit(false); setName(s.name); }}><X className="h-4 w-4" /></Button>
          </>
        ) : sure ? (
          <>
            <Button size="sm" variant="destructive" className="h-8" onClick={() => run(() => api.delete(`/admin/subcategories/${s.id}`), "Sub-category deleted")} data-testid={`subcategory-delete-confirm-${s.id}`}>Delete</Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setSure(false)}>Keep</Button>
          </>
        ) : (
          <>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit(true)} data-testid={`subcategory-rename-${s.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSure(true)} data-testid={`subcategory-delete-${s.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
          </>
        )}
      </div>
    </div>
  );
}

export function SubcategoriesDialog({ open, onClose, onChanged, counts }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/admin/subcategories").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    if (open) load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = () => {
    load();
    onChanged();
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/subcategories", { name });
      setName("");
      toast.success("Sub-category added");
      changed();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="subcategories-dialog">
        <DialogHeader><DialogTitle>Peptide sub-categories</DialogTitle></DialogHeader>
        <form onSubmit={add} className="flex gap-2">
          <Input required placeholder="e.g. Recovery" value={name} onChange={(e) => setName(e.target.value)} data-testid="subcategory-name-input" />
          <Button type="submit" className="gap-1.5" data-testid="subcategory-add-button"><Plus className="h-4 w-4" />Add</Button>
        </form>
        <div className="divide-y divide-border">
          {list.map((s) => <SubRow key={s.id} s={s} count={counts[s.id] || 0} onChanged={changed} />)}
          {list.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground" data-testid="subcategories-empty">No sub-categories yet.</p>}
        </div>
        <p className="text-xs text-muted-foreground">Pick sub-categories for each peptide in its edit window. Deleting one removes it from those peptides.</p>
      </DialogContent>
    </Dialog>
  );
}
